'use client';

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Users,
  Heart,
  Utensils,
  Mountain,
  Camera,
  Waves,
  Accessibility,
  Baby,
  Sparkles,
  Landmark,
  Music,
  Compass,
  Map,
  HeartHandshake,
  Laptop,
} from "lucide-react";
import { type LocaleType } from "@/lib/metadata";
import { Title } from '@/components/ui/Headings';

interface Theme {
  icon: string;
  title: string;
  description: string;
  extendedDescription?: string;
  whoIsThisFor?: string;
  image: string;
  features: string[];
}

interface TravelThemesClientProps {
  themes: Theme[];
  locale: LocaleType;
}

// Icon mapping object
const iconMap = {
  Users,
  Heart,
  Utensils,
  Mountain,
  Camera,
  Waves,
  Accessibility,
  Baby,
  Sparkles,
  Landmark,
  Music,
  Compass,
  Map,
  HeartHandshake,
  Laptop,
} as const;

export default function TravelThemesClient({ themes, locale }: TravelThemesClientProps) {
  const [selectedTheme, setSelectedTheme] = useState(0);

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      {/* Sidebar Navigation - Always Visible */}
      <div className="w-full lg:w-1/3 xl:w-1/4">
        <div className="bg-white rounded-2xl shadow-lg p-3 lg:p-4 sticky top-24 lg:top-32 max-h-[calc(100vh-7rem)] flex flex-col relative">
          {/* Vertical list for themes with scroll - Always visible */}
          <nav className="space-y-0.5 flex-1 overflow-y-auto pr-2 -mr-2 custom-scrollbar">
            {themes.map((theme, index) => (
              <button
                key={index}
                onClick={() => setSelectedTheme(index)}
                className={`w-full text-left px-2 py-1.5 rounded-lg transition-all duration-300 flex items-center space-x-2 group border ${
                  selectedTheme === index
                    ? 'bg-primary-100 text-primary-900 border-primary-200 shadow-sm'
                    : 'text-black-600 hover:bg-gray-50 hover:text-primary-700 border-transparent'
                }`}
              >
                {React.createElement(iconMap[theme.icon as keyof typeof iconMap], { className: `h-3.5 w-3.5 flex-shrink-0 transition-colors duration-300 ${
                  selectedTheme === index ? 'text-primary-600' : 'text-gray-400 group-hover:text-primary-500'
                }` })}
                <span className="text-xs font-medium">{theme.title}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="lg:w-2/3 xl:w-3/4">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-500 ease-in-out">
          <div className="p-2 sm:p-4 lg:p-6">
            {/* Title and Image Row */}
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 mb-6 lg:mb-8">
              {/* Theme Header - Left Side */}
              <div className="flex-1 lg:w-1/2">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    {React.createElement(iconMap[themes[selectedTheme].icon as keyof typeof iconMap], { className: "h-4 w-4 sm:h-5 sm:w-5 text-primary-600" })}
                  </div>
                  <Title size="md" className="leading-tight">
                    {themes[selectedTheme].title}
                  </Title>
                </div>

                {/* Theme Content */}
                <div className="space-y-1 lg:space-y-2">
                  {/* Description */}
                  <p className="text-gray-900 leading-relaxed text-sm">
                    {themes[selectedTheme].description}
                  </p>
                  
                  {/* Extended Description */}
                  {themes[selectedTheme].extendedDescription && (
                    <p className="text-gray-900 leading-relaxed text-sm">
                      {themes[selectedTheme].extendedDescription}
                    </p>
                  )}
                </div>
              </div>

              {/* Image and Extra Info - Right Side */}
              <div className="w-full lg:w-1/2 flex flex-col gap-4">
                {/* Large Theme Image */}
                <div className="relative w-full h-48 sm:h-56 lg:h-64 rounded-xl lg:rounded-2xl overflow-hidden flex-shrink-0 shadow-lg">
                  <Image
                    src={themes[selectedTheme].image}
                    alt={themes[selectedTheme].title}
                    fill
                    className="object-cover transition-transform duration-700 ease-in-out hover:scale-105"
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 40vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
              </div>
            </div>

            {/* Perfect for and Why This Theme - Side by Side */}
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 mb-6 lg:mb-8">
              {/* Perfect for - Left Side */}
              {themes[selectedTheme].whoIsThisFor && (
                <div className="lg:w-1/2 bg-primary-50 border-l-4 border-primary-600 p-4 sm:p-5 rounded-r-lg">
                  <p className="text-xs font-semibold text-primary-900 mb-1">Perfect for:</p>
                  <p className="text-sm text-gray-900 leading-relaxed">
                    {themes[selectedTheme].whoIsThisFor}
                  </p>
                </div>
              )}

              {/* Why Choose This Theme - Right Side */}
              <div className={`bg-gradient-to-br from-primary-50 to-white p-4 sm:p-5 rounded-xl border border-primary-100 ${themes[selectedTheme].whoIsThisFor ? 'lg:w-1/2' : 'w-full'}`}>
                <h4 className="text-sm sm:text-base font-semibold text-primary-900 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Why This Theme?
                </h4>
                <ul className="space-y-2">
                  {themes[selectedTheme].features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-900">
                      <span className="text-primary-600 mt-0.5">•</span>
                      <span className="leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="mt-2 lg:mt-4 pt-2 lg:pt-4 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Link
                    href={`/${locale}/contact`}
                    className="bg-primary-600 text-white px-6 lg:px-8 py-2.5 lg:py-3 rounded-full text-sm lg:text-base font-semibold hover:bg-primary-550 transition-colors text-center"
                  >
                    Design My Trip
                  </Link>
                  <Link
                    href={`/${locale}/signature-journeys`}
                    className="border-2 border-primary-600 text-primary-600 px-6 lg:px-8 py-2.5 lg:py-3 rounded-full text-sm lg:text-base font-semibold hover:bg-primary-600 hover:text-white transition-all text-center"
                  >
                    Browse Journeys
                  </Link>
                </div>
                <Link
                  href={`/${locale}/journal`}
                  className="text-gray-700 text-sm font-medium hover:text-primary-600 transition-colors flex items-center gap-1"
                >
                  Read our travel guide →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
